import React from 'react';
import uuid from 'uuid';
import { DocumentNode } from 'graphql';
import { isEqual } from 'lodash';

import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
import Dialog from 'components/Dialog';
import Heading from 'components/Heading';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Notice from 'components/Notice';
import Row from 'components/Row';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';
import TimeInputRow from 'coaching/components/CoachAvailabilityView/TimeInputRow';
import { consolidateTimeIntervals } from 'coaching/lib/timeIntervals';
import { userReadableDateFormat, dateOnlyFormatToDayOfWeek } from 'lib/dateTimeFormat';
import useTimeIntervals from 'coaching/hooks/useTimeIntervals';
import { SingleDayCoachAvailability } from 'coaching/containers/CoachAvailabilityContainer';
import { saveSingleDayAvailabilityOverride } from 'coaching/actions/CoachingActions';

export type Props = {
  singleDayAvailability?: SingleDayCoachAvailability;
  timezone: string; // America/Los_Angeles
  onRequestHide: () => void;
  availabilityQuery?: { query: DocumentNode; variables?: Object };
};

export default function SaveSingleDayOverrideDialog({
  singleDayAvailability,
  availabilityQuery,
  onRequestHide,
}: Props) {
  if (!singleDayAvailability) {
    return (
      <Dialog size="xsmall" onRequestHide={onRequestHide}>
        <BoxBody>
          Oops! Looks like something went wrong. Please refresh your browser and try again.
        </BoxBody>
      </Dialog>
    );
  }

  // we can't directly use `singleDayAvailability.availableTimeframes`
  // because otherwise, the `useTimeIntervals` hook will actually mutate
  // the array itself and the new intervals will appear in the `singleDayAvailability`
  // object in the cache even if the user does not save them
  const initialIntervals =
    singleDayAvailability.availableTimeframes.length > 0
      ? [...singleDayAvailability.availableTimeframes]
      : [];
  const {
    intervals,
    addInterval,
    removeInterval,
    updateIntervalStartTime,
    updateIntervalEndTime,
    defaultInterval,
  } = useTimeIntervals(initialIntervals);

  const handleSaveOverride = async () => {
    const mergedIntervals = consolidateTimeIntervals(intervals);
    await saveSingleDayAvailabilityOverride(
      {
        uuid: singleDayAvailability.override?.uuid ?? uuid.v4(),
        date: singleDayAvailability.date,
        availabilityRules: mergedIntervals.map(i => ({
          uuid: uuid.v4(),
          startTime: i.startTime,
          endTime: i.endTime,
          dayOfWeek: dateOnlyFormatToDayOfWeek(singleDayAvailability.date),
        })),
      },
      availabilityQuery
    );
    onRequestHide();
  };

  const userHasModifiedExistingAvailability = !isEqual(initialIntervals, intervals);
  return (
    <Dialog size="xsmall" onRequestHide={onRequestHide}>
      <BoxBody>
        <Row>
          <Heading serif>Edit availability</Heading>
        </Row>
        <Row size="xsmall">
          <Text size="xsmall">
            <MediaObject center figure="right">
              <Text weight="semibold" align="left">
                {userReadableDateFormat(singleDayAvailability.date)}
              </Text>
              <Link
                data-test="add-hours-action"
                weight="semibold"
                onClick={() => {
                  addInterval(defaultInterval, intervals.length);
                }}>
                Add hours
              </Link>
            </MediaObject>
          </Text>
        </Row>
        <Row>
          {intervals.length === 0 && userHasModifiedExistingAvailability ? (
            <NoIntervalEmptyState />
          ) : (
            intervals.map((interval, index) => (
              <TimeInputRow
                key={`${interval.startTime}-${index}`}
                interval={interval}
                index={index}
                onRemoveInterval={removeInterval}
                onUpdateIntervalEndTime={updateIntervalEndTime}
                onUpdateIntervalStartTime={updateIntervalStartTime}
              />
            ))
          )}
        </Row>
        <Buttons axis="y">
          <Button
            level="primary"
            color="mascot"
            onClick={handleSaveOverride}
            data-test="apply-changes-button">
            Apply changes
          </Button>
          <Link>
            <Text weight="semibold" size="xsmall" onClick={onRequestHide} data-test="cancel-button">
              Cancel
            </Text>
          </Link>
        </Buttons>
      </BoxBody>
    </Dialog>
  );
}

function NoIntervalEmptyState() {
  return (
    <Text size="xsmall" align="left">
      <Row size="small">Not available</Row>
      <Notice color="paper" noShadow>
        <TextWithIcon
          icon="explanation-circle"
          iconColor="chalkboard"
          center={false}
          lineHeight="medium"
          color="chalkboard">
          Removing hours may cause scheduled sessions to be cancelled.
        </TextWithIcon>
      </Notice>
    </Text>
  );
}
